import type {
  OrganizationDetail,
  OrganizationFrame,
  OrganizationUpdatePayload,
} from "@kontextwerk/control-web-client"
import { getAdminApi } from "../client"
import { loadKehrblattOrganizationFrame, type KehrblattFrameMember } from "../organization-frame"
import { captureMembershipSettings, type CapturedMembershipSettings } from "../membership-settings"

export type OrganizationUserListQuery = {
  query?: string | null
  offset?: number | null
  limit?: number | null
}

export type OrganizationMemberListResponse = {
  items: KehrblattFrameMember[]
  total: number
}

const organizationUsersInflight = new Map<
  string,
  { binding: CapturedMembershipSettings; request: Promise<OrganizationMemberListResponse> }
>()

const usersCacheKey = (
  orgId: string,
  query: OrganizationUserListQuery | undefined,
  binding: CapturedMembershipSettings,
) =>
  JSON.stringify({
    orgId,
    issuer: binding.session.product_issuer,
    sessionId: binding.session.session_id,
    revision: binding.session.context_revision,
    query: query?.query ?? null,
    offset: query?.offset ?? null,
    limit: query?.limit ?? null,
  })

export const resetOrganizationProjects = (orgId: string) =>
  getAdminApi().organization.provisioning.resetProjects(orgId)

export const fetchOrganization = (orgId: string): Promise<OrganizationDetail> =>
  getAdminApi().organization.registry.get(orgId)

export const fetchOrganizationFrame = (orgId: string): Promise<OrganizationFrame> =>
  getAdminApi().organization.registry.getFrame(orgId)

export const updateOrganization = (orgId: string, payload: OrganizationUpdatePayload) =>
  getAdminApi()
    .organization.registry.update(orgId, payload)
    .then((response) => {
      invalidateOrganizationCaches(orgId)
      invalidateOrganizationCaches(response?.id)

      return response
    })

const loadOrganizationMembers = async (
  orgId: string,
  query: OrganizationUserListQuery | undefined,
  binding: CapturedMembershipSettings,
): Promise<OrganizationMemberListResponse> => {
  const offset = query?.offset ?? 0
  const limit = query?.limit

  if (
    !Number.isSafeInteger(offset) ||
    offset < 0 ||
    (limit != null && (!Number.isSafeInteger(limit) || limit < 1))
  )
    throw new TypeError("Invalid organization roster pagination")

  if (binding.signal.aborted || !binding.current())
    throw new DOMException("Obsolete organization roster", "AbortError")

  const frame = await loadKehrblattOrganizationFrame(orgId, {})

  if (binding.signal.aborted || !binding.current())
    throw new DOMException("Obsolete organization roster", "AbortError")

  if (frame.id !== orgId) throw new TypeError("Foreign organization roster")

  const search = query?.query?.trim().toLocaleLowerCase("de") ?? ""
  const members = frame.users.filter(
    (member) =>
      !search ||
      [
        member.id,
        member.membership_id,
        member.username,
        `${member.first_name} ${member.last_name}`,
      ].some((value) => value.toLocaleLowerCase("de").includes(search)),
  )

  return {
    items: members.slice(offset, limit == null ? undefined : offset + limit),
    total: members.length,
  }
}

export const fetchOrganizationUsers = async (
  orgId: string,
  query?: OrganizationUserListQuery,
): Promise<OrganizationMemberListResponse> =>
  loadOrganizationMembers(orgId, query, captureMembershipSettings(orgId))

export const fetchOrganizationUsersCached = async (
  orgId: string,
  query?: OrganizationUserListQuery,
): Promise<OrganizationMemberListResponse> => {
  const binding = captureMembershipSettings(orgId)
  const key = usersCacheKey(orgId, query, binding)
  const inflight = organizationUsersInflight.get(key)

  if (inflight && !inflight.binding.signal.aborted && inflight.binding.current())
    return inflight.request

  // Share concurrent reads only within one confirmed context, never settled roster data.
  const request = loadOrganizationMembers(orgId, query, binding).finally(() => {
    if (organizationUsersInflight.get(key)?.request === request)
      organizationUsersInflight.delete(key)
  })
  organizationUsersInflight.set(key, { binding, request })

  return request
}

export const invalidateOrganizationCaches = (orgId?: string | null) => {
  if (!orgId) {
    organizationUsersInflight.clear()

    return
  }

  for (const key of organizationUsersInflight.keys()) {
    if (key.includes(`"orgId":"${orgId}"`)) organizationUsersInflight.delete(key)
  }
}
